import FormLabel from '@mui/material/FormLabel';
import { Theme } from '@mui/system/createTheme';
import useMediaQuery from '@mui/system/useMediaQuery';
import {
  DateField as MuiDateField,
  DateFieldProps as MuiDateFieldProps,
} from '@mui/x-date-pickers/DateField';
import {
  DatePicker as MuiDatePicker,
  DatePickerProps as MuiDatePickerProps,
} from '@mui/x-date-pickers/DatePicker';
import {
  StaticDatePicker as MuiStaticDatePicker,
  StaticDatePickerProps as MuiStaticDatePickerProps,
} from '@mui/x-date-pickers/StaticDatePicker';
import {
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subYears,
} from 'date-fns';
import React, { forwardRef, useMemo, useRef, useState } from 'react';

import { Box } from './Box';

type OmittedProps = Omit<
  MuiDatePickerProps<Date>,
  'open' | 'onOpen' | 'onClose'
>;

export interface DatePickerProps extends OmittedProps {
  label: React.ReactNode;
  floatingLabel?: boolean;
  size?: MuiDateFieldProps<Date>['size'];
  helperText?: string;
  error?: boolean;
  required?: boolean;
  clearable?: boolean;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  label,
  floatingLabel,
  size,
  helperText,
  error,
  required,
  clearable,
  slotProps = {},
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const anchorElRef = useRef(null);
  const fieldProps = {
    label,
    floatingLabel,
    size,
    helperText,
    error,
    required,
    open: () => setOpen(true),
    ...slotProps.field,
  } as any;

  return (
    <Box ref={anchorElRef}>
      <MuiDatePicker
        slots={{
          // eslint-disable-next-line react/display-name, @typescript-eslint/no-unused-vars
          field: forwardRef((fieldProps, ref) => {
            return (
              <DateField
                {...fieldProps}
                label={label}
                floatingLabel={floatingLabel}
                size={size}
                helperText={helperText}
                required={required}
                clearable={clearable}
              />
            );
          }),
          ...props.slots,
        }}
        slotProps={{
          field: fieldProps,
          popper: {
            anchorEl: anchorElRef.current,
            ...slotProps.popper,
          },
          ...slotProps,
        }}
        {...props}
        open={open}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
      />
    </Box>
  );
};

DatePicker.displayName = 'DatePicker';

interface DateFieldProps extends MuiDateFieldProps<Date> {
  floatingLabel?: boolean;
  required?: boolean;
}

export const DateField: React.FC<DateFieldProps> = ({ ...props }) => {
  const {
    helperText,
    label,
    floatingLabel,
    sx,
    size,
    fullWidth = true,
    required,
    ...otherProps
  } = props;

  return (
    <FormLabel
      sx={[
        { display: 'block' },
        fullWidth && { width: '100%' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {!floatingLabel && label && (
        <Box sx={{ mb: 1 }}>
          {label}
          {required && <Box component="span"> *</Box>}
        </Box>
      )}
      <MuiDateField
        {...otherProps}
        helperText={helperText}
        label={floatingLabel && label}
        size={size}
        fullWidth={fullWidth}
        hiddenLabel={!floatingLabel}
        required={required}
        InputProps={{
          sx: { boxShadow: 'none' },
          ...props.InputProps,
        }}
      />
    </FormLabel>
  );
};

DateField.displayName = 'DateField';

interface StaticDatePickerProps extends MuiStaticDatePickerProps<Date> {
  toolbarTitle?: string;
}

export const StaticDatePicker: React.FC<StaticDatePickerProps> = ({
  toolbarTitle,
  ...props
}) => {
  const today = useMemo(() => new Date(), []);
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const shortcuts = useMemo(
    () => [
      {
        label: '7 days ago',
        getValue: () => subDays(today, 6),
      },
      {
        label: 'Start of last week',
        getValue: () => {
          const lastWeekStart = subDays(today, today.getDay() + 7);
          return lastWeekStart;
        },
      },
      {
        label: 'Start of this week',
        getValue: () => {
          return startOfWeek(today);
        },
      },
      {
        label: '30 days ago',
        getValue: () => subDays(today, 29),
      },
      {
        label: 'Start of last month',
        getValue: () => {
          const lastMonth = subMonths(today, 1);
          return startOfMonth(lastMonth);
        },
      },
      {
        label: '90 days ago',
        getValue: () => subDays(today, 89),
      },
      {
        label: 'Start of last quarter',
        getValue: () => {
          const lastQuarter = subQuarters(today, 1);
          return startOfQuarter(lastQuarter);
        },
      },
      {
        label: 'Start of this quarter',
        getValue: () => {
          return startOfQuarter(today);
        },
      },
      {
        label: 'Start of last year',
        getValue: () => startOfYear(subYears(today, 1)),
      },
      {
        label: 'Start of this year',
        getValue: () => startOfYear(today),
      },
    ],
    [today]
  );

  return (
    <Box
      sx={{
        p: mdDown ? 1 : 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <MuiStaticDatePicker
        localeText={{ toolbarTitle: toolbarTitle }}
        slotProps={{
          shortcuts: {
            items: smDown ? [] : shortcuts,
          },
        }}
        {...props}
      />
    </Box>
  );
};
